import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { useContext, useState } from "react";
import Collapse from "react-bootstrap/Collapse";
import Drawer from "~/components/drawers/drawer";
import EmailDomainErrors from "~/components/EmailDomains/EmailDomainErrors";
import EmailDomainRecordSection from "~/components/EmailDomains/EmailDomainRecordSection";
import GlobalContainer from "~/components/global_container";
import { NOOP_FALSE } from "~/constants/callbacks";
import DrawerContext from "~/contexts/DrawerContext";
import useEmailDomainRequest from "~/hooks/useEmailDomainRequest";
import emailDomainStatus from "~/lib/emailDomainStatus";
import emailDomainsApi from "~/lib/emailDomainsApi";

// A different drawer domain starts a fresh request scope and local DNS snapshot.
const EmailDomainDrawer = (props) => {
  return <EmailDomainDrawerContent key={props.domain.id} {...props} />;
};
const EmailDomainDrawerContent = ({
  domain: initialDomain,
  onDomainUpdated = NOOP_FALSE,
  onVerifyDomain,
}) => {
  const [domain, setDomain] = useState(initialDomain);
  const [isOpen, setIsOpen] = useState(false);
  const { closeDrawer } = useContext(DrawerContext);
  const { run, busy, errors } = useEmailDomainRequest(domain.id);
  const [operation, setOperation] = useState(null);
  const updateDomain = async (action) => {
    if (busy) {
      return;
    }
    setOperation(action);
    const updated = await run((options) =>
      action === "verify"
        ? onVerifyDomain(domain.id)
        : emailDomainsApi.refresh(domain.id, options),
    );
    if (!updated) {
      return;
    }
    setDomain(updated);
    if (action === "refresh") {
      onDomainUpdated(updated);
    }
    if (action === "verify") {
      GlobalContainer.notify(
        "Verifying DNS settings. This may take some time",
        "success",
      );
    }
  };
  const trackingStatus = emailDomainStatus(domain);
  const dmarcRecords = [
    {
      record_type: "TXT",
      name: "_dmarc",
      value: "v=DMARC1; p=none; pct=100;",
      valid: !!domain.dmarc_record,
    },
  ];
  return (
    <Drawer size="normal" id="sending-domains">
      <div className="modal-header bg-light bt mt-4">
        <div className="modal-title">
          <small className="text-grey">
            Authenticate <strong>{domain.name}</strong> with Brokerkit by
            modifying your domain&apos;s DNS records. These changes allow your
            bulk/campaign emails to appear to come from{" "}
            <strong>{domain.name}</strong>, instead of from our servers. After
            you&apos;ve made the required DNS changes, please wait 24-48 hours
            for the changes to propagate.{" "}
            <a
              href="https://support.brokerkit.app/hc/help/articles/configuring-email-sending-domains"
              target="_blank"
              className="text-green"
              rel="noopener noreferrer"
            >
              Learn how to do that?
            </a>
          </small>
        </div>
      </div>
      <div className="modal-body p0">
        <EmailDomainRecordSection
          title="Sending Records"
          required
          domain={domain}
          records={domain.sending_records}
          spfTooltip
          description="To configure the DNS settings for your email sending domain (e.g. domain.com in name@domain.com)."
        />
        <hr />
        <EmailDomainRecordSection
          title="Tracking Record"
          required
          domain={domain}
          records={domain.tracking_records}
          description="To track clicks and opens on your bulk, blast, and campaign emails sent through this sending domain."
        >
          <div className="px-4 pt-3 pb-3">
            <h5>Secure tracking (HTTPS)</h5>
            <p>
              Brokerkit sets up HTTPS automatically after your tracking record
              is verified. No additional DNS record is needed.
            </p>
            <span
              className={`badge ${domain.ready_for_campaigns === true ? "badge-outline-green" : "badge-outline-warning"}`}
            >
              {trackingStatus.label}
            </span>
            <p className="mt10">{trackingStatus.description}</p>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={busy}
              onClick={() => updateDomain("refresh")}
            >
              {busy && operation === "refresh"
                ? "Refreshing..."
                : "Refresh status"}
            </button>
          </div>
        </EmailDomainRecordSection>
        <hr />
        <button
          type="button"
          className="btn btn-link ml-4 text-green shadow-none"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((open) => !open)}
        >
          {isOpen ? "Hide Advanced Settings" : "Advanced Settings"}
        </button>
        <Collapse className="mt-3" in={isOpen}>
          <div>
            <EmailDomainRecordSection
              title="DMARC Record"
              domain={domain}
              records={dmarcRecords}
              description={
                <>
                  A DMARC record is not required but highly recommended for
                  reliable email delivery.{" "}
                  <a
                    href="https://support.brokerkit.app/hc/help/articles/implementing-dmarc"
                    target="_blank"
                    className="text-green"
                    rel="noopener noreferrer"
                  >
                    Learn more
                  </a>
                </>
              }
            />
            <hr />
            <EmailDomainRecordSection
              title="Receiving Records"
              domain={domain}
              records={domain.receiving_records}
              receiving
              description="MX records are not required but highly recommended for reliable email delivery. Properly configured MX records can also improve email deliverability by increasing your email's trustworthiness and reducing the likelihood of it being marked as spam."
            />
          </div>
        </Collapse>
        <div className="my-3 px-4">
          <EmailDomainErrors errors={errors} />
          <button
            type="button"
            className="btn btn-success"
            disabled={busy}
            onClick={() => updateDomain("verify")}
          >
            <FontAwesomeIcon
              icon={
                busy && operation === "verify"
                  ? "far fa-spinner"
                  : ["fas", "key"]
              }
              pulse={busy && operation === "verify"}
            />{" "}
            {busy && operation === "verify"
              ? "Authenticating..."
              : "Authenticate Domain"}
          </button>
          <button
            type="button"
            className="btn btn-secondary ml-4"
            onClick={closeDrawer}
          >
            Cancel
          </button>
        </div>
      </div>
    </Drawer>
  );
};
EmailDomainDrawer.propTypes = {
  domain: PropTypes.object.isRequired,
  onDomainUpdated: PropTypes.func,
  onVerifyDomain: PropTypes.func.isRequired,
};
EmailDomainDrawerContent.propTypes = EmailDomainDrawer.propTypes;

export default EmailDomainDrawer;
